// Customer Bridge Service - Calls Customer Marketplace API (vendor-updates-table)
const LAMBDA_URL = process.env.REACT_APP_CUSTOMER_BRIDGE_LAMBDA_URL || 'https://nzjfm29c8i.execute-api.us-east-1.amazonaws.com/prod';

/**
 * Get real vendor data from Customer Marketplace API (vendor-updates-table)
 */
export const getRealVendorsFromPointB = async () => {
  try {
    console.log('🔄 Fetching real vendors from Customer Marketplace API...');
    console.log('Lambda URL:', LAMBDA_URL);
    
    // Call our Customer Marketplace API
    const response = await fetch(`${LAMBDA_URL}/vendors`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.vendors || !Array.isArray(result.vendors)) {
      throw new Error('Invalid response format: missing vendors array');
    }

    console.log(`✅ Real vendors fetched from Customer Marketplace API:`, result.vendors);
    return result.vendors;

  } catch (error) {
    console.error(`❌ Error fetching real vendors from Customer Marketplace API:`, error);
    throw new Error(`Failed to fetch real vendors: ${error.message}`);
  }
};

/**
 * Get vendor data filtered by vendorId
 */
export const getVendorById = async (vendorId) => {
  try {
    console.log(`🔄 Fetching vendor ${vendorId} from Customer Marketplace API...`);
    
    // Get all vendors and filter by ID
    const allVendors = await getRealVendorsFromPointB();
    const vendor = allVendors.find(v => v.vendorId === vendorId);
    
    if (!vendor) {
      throw new Error(`Vendor not found: ${vendorId}`);
    }

    console.log(`✅ Vendor ${vendorId} fetched from Customer Marketplace API:`, vendor);
    return vendor;

  } catch (error) {
    console.error(`❌ Error fetching vendor ${vendorId}:`, error);
    throw new Error(`Failed to fetch vendor: ${error.message}`);
  }
};

/**
 * Transform Customer Marketplace API vendor data to UI-friendly format
 */
export const transformEventBridgeVendors = (vendorData) => {
  try {
    console.log('🔄 Transforming Customer Marketplace API vendor data for UI...');
    
    if (!vendorData || !Array.isArray(vendorData)) {
      console.warn('No vendor data to transform');
      return [];
    }

    // Transform each vendor to UI-expected format
    const vendors = vendorData.map(vendor => {
      return {
        // Map API fields to UI fields
        id: vendor.vendorId || `vendor-${Date.now()}`,
        name: vendor.businessName || 'Unknown Vendor',
        type: vendor.posSystem || 'restaurant',
        description: `Vendor from ${vendor.location?.address?.locality || 'Unknown Location'}`,
        location: vendor.location?.address?.locality || 'Unknown Location',
        
        // Provide sensible defaults for missing fields
        rating: 4.0, // Default rating
        deliveryTime: '20-30 min', // Default delivery time
        isOpen: true, // Assume vendors are open
        specialties: ['General'], // Default specialties
        featured: false, // Default to not featured
        
        // Map existing fields
        posSystem: vendor.posSystem || 'unknown',
        source: 'Customer Marketplace API (vendor-updates-table)',
        onboardingDate: vendor.createdAt || new Date().toISOString(),
        
        // Build contact info object
        contactInfo: {
          email: vendor.email || '',
          phone: vendor.location?.phoneNumber || '',
          address: vendor.location?.address || {}
        },
        
        // Map menu data (if available)
        menu: vendor.menu || {},
        
        // Add Square integration status
        squareConnected: vendor.squareConnected || false,
        merchantId: vendor.merchantId || null,
        
        // Preserve original vendor data for debugging
        _originalData: vendor
      };
    });

    console.log(`✅ Transformed ${vendors.length} vendors for UI`);
    return vendors;

  } catch (error) {
    console.error('❌ Error transforming vendor data:', error);
    return [];
  }
};

/**
 * Get all real vendors with fallback to hardcoded data
 */
export const getAllRealVendors = async () => {
  try {
    console.log('🔄 Attempting to fetch real vendors from Customer Marketplace API...');
    
    // Try to get real vendor data first
    const realVendors = await getRealVendorsFromPointB();
    const transformedVendors = transformEventBridgeVendors(realVendors);
    
    if (transformedVendors.length > 0) {
      console.log(`✅ Successfully loaded ${transformedVendors.length} real vendors from Customer Marketplace API`);
      return transformedVendors;
    } else {
      console.log('⚠️ No real vendors found, this might indicate an issue with Customer Marketplace API');
      return [];
    }

  } catch (error) {
    console.error('❌ Failed to fetch real vendors, Customer Marketplace API may not be working:', error);
    return [];
  }
};

/**
 * Check if Customer Marketplace API is accessible
 */
export const checkPointBHealth = async () => {
  try {
    console.log('🏥 Checking Customer Marketplace API health...');
    
    const response = await fetch(`${LAMBDA_URL}/vendors`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const isHealthy = response.ok;
    console.log(`Customer Marketplace API health check: ${isHealthy ? '✅ HEALTHY' : '❌ UNHEALTHY'}`);
    
    return {
      healthy: isHealthy,
      status: response.status,
      statusText: response.statusText
    };

  } catch (error) {
    console.error('❌ Customer Marketplace API health check failed:', error);
    return {
      healthy: false,
      error: error.message
    };
  }
};
